import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { toast } from 'react-toastify';
import { formatDateLongRu } from '../../utils/dateUtils';
import { displayTrainerName, displayGroupRole } from '../../utils/userDisplayName';
import { Calendar, Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import Modal from '../../components/Modal';
import ScheduleForm from '../../components/ScheduleForm';
import './GroupSchedulePage.css';

const GroupSchedulePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);

    const { data: group, isLoading: groupLoading } = useQuery(
        ['group', id],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/groups/${id}`, {
                withCredentials: true
            });
            return response.data;
        }
    );

    const { data: schedule, isLoading: scheduleLoading, refetch } = useQuery(
        ['groupSchedule', id],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/schedule/group/${id}`, {
                withCredentials: true
            });
            return response.data;
        }
    );

    const handleCreateEvent = () => {
        setEditingEvent(null);
        setShowModal(true);
    };

    const handleEditEvent = (event) => {
        setEditingEvent(event);
        setShowModal(true);
    };

    const handleEventSuccess = () => {
        setShowModal(false);
        setEditingEvent(null);
        refetch();
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm('Вы уверены, что хотите удалить это событие?')) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/schedule/${eventId}`, {
                withCredentials: true
            });
            toast.success('Событие удалено');
            refetch();
        } catch (error) {
            console.error('Ошибка удаления события:', error);
            toast.error(error.response?.data?.message || 'Ошибка удаления');
        }
    };

    if (groupLoading || scheduleLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="group-schedule-page">
            <div className="page-header">
                <button className="btn btn-secondary" onClick={() => navigate('/')}>
                    <ArrowLeft size={18} />
                    Назад к группам
                </button>
                <div>
                    <h1>
                        <Calendar size={28} />
                        Расписание группы
                    </h1>
                    <p>{group?.nomination}</p>
                </div>
                <button className="btn btn-primary" onClick={handleCreateEvent}>
                    <Plus size={18} />
                    Добавить встречу
                </button>
            </div>

            {!schedule || schedule.length === 0 ? (
                <div className="empty-state">
                    <Calendar size={64} className="empty-icon" />
                    <h3>Расписание пока пустое</h3>
                    <p>Добавьте первую встречу в расписание группы</p>
                    <button className="btn btn-primary btn-large" onClick={handleCreateEvent}>
                        <Plus size={20} />
                        Добавить первую встречу
                    </button>
                </div>
            ) : (
                <div className="schedule-list">
                    {schedule.map(event => (
                        <div key={event.id} className="schedule-card">
                            <div className="schedule-header">
                                <div className="cycle-badge">Цикл {event.cycle}</div>
                                <div className="schedule-actions">
                                    <button className="btn-icon" onClick={() => handleEditEvent(event)} title="Редактировать">
                                        <Edit size={16} />
                                    </button>
                                    <button className="btn-icon" onClick={() => handleDeleteEvent(event.id)} title="Удалить">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            <h3>{event.theme}</h3>

                            <div className="schedule-dates">
                                <strong>Даты:</strong> {formatDateLongRu(event.start_date)}
                                {' - '}
                                {formatDateLongRu(event.end_date)}
                            </div>

                            {event.trainers && event.trainers.length > 0 && (
                                <div className="schedule-trainers">
                                    <strong>Тренеры:</strong>
                                    <div className="trainers-list">
                                        {event.trainers.map((trainer, idx) => (
                                            <span key={idx} className="trainer-tag">
                                                {displayTrainerName(trainer)} ({displayGroupRole(trainer.role)})
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingEvent ? 'Редактировать встречу' : 'Добавить встречу'}
                size="medium"
            >
                <ScheduleForm
                    event={editingEvent}
                    groupId={id}
                    onSuccess={handleEventSuccess}
                    onCancel={() => setShowModal(false)}
                />
            </Modal>
        </div>
    );
};

export default GroupSchedulePage;

